import React, { useCallback, useEffect, useRef, useState } from 'react';
import { List as VirtualizedList, AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized';

import './_list.scss'
import './_list.dark-mode.scss'

import PropTypes from 'prop-types';
import { UI_NAME } from '../../../utils/config';

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: isSafari ? 28 : 30,
});

const List = ({ data, rowRender, maxHeight }) => {
  const listRef = useRef(null);
  const [listHeight, setListHeight] = useState(cache.defaultHeight);
  const updateTimeoutRef = useRef(null);

  const updateTotalHeight = useCallback(() => {
    const measuredHeight = data.reduce((acc, _, index) => acc + (cache.getHeight(index) || cache.defaultHeight), 0);

    const newHeight = Math.min(measuredHeight, maxHeight);

    setListHeight((prev) => {
      if (prev !== newHeight && measuredHeight > 0) {
        return newHeight;
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, maxHeight]);

  useEffect(() => {
    cache.clearAll();
    listRef.current?.recomputeRowHeights();
    listRef.current?.forceUpdateGrid();

    const initialHeight = Math.min(
      data.length * cache.defaultHeight,
      maxHeight,
    );
    setListHeight(initialHeight);

    updateTimeoutRef.current = setTimeout(() => {
      updateTotalHeight();
    }, 50);

    return () => clearTimeout(updateTimeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, maxHeight]);

  const rowRenderer = ({ index, key, parent, style }) => {
    const item = data[index];

    return (
      <CellMeasurer
        key={key}
        cache={cache}
        columnIndex={0}
        parent={parent}
        rowIndex={index}
      >
        {({ measure }) => (
          <div style={style} ref={measure}>
            {rowRender({ item, index })}
          </div>
        )}
      </CellMeasurer>
    );
  };

  return (
    <div
      className={`${UI_NAME}-list`}
      style={{ maxHeight, overflow: 'auto' }}
    >
      <AutoSizer disableHeight>
        {({ width }) => (
          <VirtualizedList
            ref={listRef}
            width={width}
            height={listHeight}
            deferredMeasurementCache={cache}
            rowHeight={cache.rowHeight}
            rowRenderer={rowRenderer}
            rowCount={data.length}
            overscanRowCount={5}
            onRowsRendered={updateTotalHeight}
          />
        )}
      </AutoSizer>
    </div>
  );
};

List.defaultProps = {
  maxHeight: 800,
}

List.propTypes = {
  data: PropTypes.array,
  rowRender: PropTypes.func,
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default List;
